/**
 * Reset Password Page
 * Public page reached from the password-reset email link (/reset-password/:token)
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { AuthLayout, AuthBrand } from './AuthLayout';
import { resetPassword } from '@/services/auth.service';

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Липсва или невалиден токен за възстановяване');
      return;
    }
    if (newPassword.length < 8) {
      setError('Паролата трябва да е поне 8 символа');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Паролите не съвпадат');
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(token, newPassword);
      toast.success('Паролата е сменена успешно. Влезте с новата парола.');
      navigate('/login', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Грешка при възстановяване';
      setError(
        message === 'Invalid or expired reset token'
          ? 'Връзката за възстановяване е невалидна или изтекла'
          : message
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <AuthBrand subtitle="Нова парола" />

      {error && (
        <div className="auth-error px-4 py-3 rounded-xl">
          <p className="text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <div className="space-y-4">
          <Input
            label="Нова парола"
            type="password"
            name="new-password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="--------"
            required
          />

          <Input
            label="Потвърди новата парола"
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="--------"
            required
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          fullWidth
          className="py-3 rounded-xl font-semibold"
        >
          Смени паролата
        </Button>

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="w-full text-center text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors"
        >
          Обратно към вход
        </button>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
